import React, { useEffect, useState, memo, useMemo } from 'react';
import { motion, AnimatePresence as _AnimatePresence } from 'framer-motion';
import { useSpotifyNowPlaying, liveProgressMs } from '../hooks/useSpotifyNowPlaying';
import { formatTimeAgo } from '../utils/formatTimeAgo';
import MarqueeText from './MarqueeText';
// TypeScript 4.9 + framer-motion 11 return-type compat shim
const AnimatePresence = _AnimatePresence as React.FC<{
  mode?: 'wait' | 'sync' | 'popLayout';
  children?: React.ReactNode;
}>;

const GREEN = '#1DB954';

/* ─── Animated equalizer bars ─── */
const EqBars: React.FC = () => (
  <span className="flex items-end gap-[2px]" aria-hidden="true">
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className="w-[3px] rounded-full"
        style={{
          background: GREEN,
          height: 12,
          display: 'block',
          transformOrigin: 'bottom',
          animation: `eqBar 0.${7 + i * 2}s ease-in-out infinite alternate`,
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes eqBar {
        from { transform: scaleY(0.25); }
        to   { transform: scaleY(1); }
      }
    `}</style>
  </span>
);

/* ─── Spotify logo SVG ─── */
const SpotifyLogo: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={GREEN} aria-label="Spotify">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);

/* ─── Skeleton line ─── */
const Shimmer: React.FC<{ className?: string; dark?: boolean }> = ({ className = '', dark = false }) => (
  <div className={`rounded animate-pulse ${dark ? 'bg-white/10' : 'bg-black/10'} ${className}`} />
);

/* ─── Format ms → m:ss ─── */
function fmtMs(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/* ─── Placeholder when no album art ─── */
const AlbumPlaceholder: React.FC<{ size: number; isDarkMode: boolean }> = ({ size, isDarkMode }) => (
  <div
    className="flex items-center justify-center flex-shrink-0"
    style={{
      width: size,
      height: size,
      background: isDarkMode
        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(135deg, #fce4f3 0%, #e8d5f5 100%)',
    }}
  >
    <SpotifyLogo size={Math.round(size * 0.22)} />
  </div>
);

/* ─── Main widget ─── */
interface SpotifyWidgetProps {
  isDarkMode?: boolean;
  /** When set, expand/collapse is controlled by the parent (mobile taskbar popup). */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const SpotifyWidget: React.FC<SpotifyWidgetProps> = memo(({ isDarkMode = false, isExpanded, onExpandedChange }) => {
  const { track, isLoading, error, isEmpty, isConfigured, refresh } = useSpotifyNowPlaying();
  const isControlled = isExpanded !== undefined;
  const [isMinimizedInternal, setIsMinimizedInternal] = useState(true);
  const isMinimized = isControlled ? !isExpanded : isMinimizedInternal;
  const setMinimized = (minimized: boolean) => {
    if (isControlled) onExpandedChange?.(!minimized);
    else setIsMinimizedInternal(minimized);
  };
  const [now, setNow] = useState(() => Date.now());
  const isLive = track?.isPlaying === true;

  /* Re-render relative "X ago" labels and live progress */
  useEffect(() => {
    const ms = isLive ? 1_000 : 30_000;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [isLive]);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const liveProgress = liveProgressMs(track, now);

  const TASKBAR_H = 68;
  const isMobile  = viewport.w < 640;
  const MARGIN    = isMobile ? 10 : 12;
  const HEADER_H  = isMobile ? 38 : 40;
  const INFO_H    = isMobile ? 88 : 96;
  const MAX_ART   = isMobile ? 150 : 280;
  const MIN_ART   = isMobile ? 130 : 200;

  /* Album art size = square, constrained by both width and available height */
  const artSize = Math.max(
    MIN_ART,
    Math.min(
      MAX_ART,
      viewport.w - MARGIN * 2,
      viewport.h - TASKBAR_H - MARGIN * 2 - HEADER_H - INFO_H,
    ),
  );

  const widgetH = isMinimized ? HEADER_H : HEADER_H + artSize + INFO_H;
  const anchorStyle: React.CSSProperties = { right: MARGIN };

  /* ── Styles that match Window.tsx ── */
  const titleBarCls = isDarkMode
    ? 'bg-gray-800'
    : 'bg-gradient-to-r from-pink-200 to-purple-200';
  const shellCls = isDarkMode
    ? 'bg-gray-900 border-gray-700/50'
    : 'bg-white border-gray-200/40';
  const headlineText  = isDarkMode ? 'text-white'   : 'text-gray-800';
  const mutedText     = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const dividerCls    = isDarkMode ? 'border-gray-700' : 'border-gray-100';
  const progressTrack = isDarkMode ? 'bg-white/15'   : 'bg-black/10';
  const captionColor  = isDarkMode ? '#f9a8d4'       : '#ec4899';

  const timeAgo = track?.playedAt && !isLive
    ? formatTimeAgo(track.playedAt, now)
    : '';

  const statusText = useMemo(() => {
    if (isLoading) return 'Connecting…';
    if (error) return 'Unavailable';
    if (!isConfigured) return 'Not connected';
    if (!track) {
      if (isEmpty) return 'Not connected';
      return 'Nothing playing';
    }
    if (isLive) return 'Now playing';
    if (timeAgo) return `Last played · ${timeAgo}`;
    return 'Last played';
  }, [isLoading, error, isConfigured, track, isEmpty, isLive, timeAgo]);

  const progressPct = track?.duration
    ? Math.min(100, (liveProgress / track.duration) * 100)
    : 0;

  const timeLabel = track
    ? isLive
      ? `${fmtMs(liveProgress)} / ${fmtMs(track.duration)}`
      : fmtMs(track.duration)
    : '';

  return (
    <div
      data-window-surface="true"
      role="region"
      aria-label="Now Playing"
      style={{
        position:   'fixed',
        bottom:     TASKBAR_H + MARGIN,
        ...anchorStyle,
        width:      artSize,
        height:     widgetH,
        zIndex:     9990,
        userSelect: 'none',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className={`w-full h-full rounded-lg border shadow-lg overflow-hidden flex flex-col ${shellCls}`}
      >
        {/* ── Title bar — matches Window.tsx chrome ── */}
        <div
          className={`flex-shrink-0 flex items-center justify-between px-3 select-none ${titleBarCls}`}
          style={{ height: HEADER_H }}
          onClick={() => { if (isMinimized) setMinimized(false); }}
          role={isMinimized ? 'button' : undefined}
          tabIndex={isMinimized ? 0 : undefined}
          onKeyDown={isMinimized ? (e) => { if (e.key === 'Enter' || e.key === ' ') setMinimized(false); } : undefined}
        >
          <div className="flex items-center gap-1.5 min-w-0 flex-1 pr-1">
            <SpotifyLogo size={13} />
            <MarqueeText
              text={statusText}
              className={`text-[10px] sm:text-[11px] font-semibold leading-none tracking-wide ${headlineText}`}
            />
            {isLive && !isLoading && (
              <span className="flex-shrink-0">
                <EqBars />
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {isConfigured && (
              <motion.button
                onClick={refresh}
                className={`opacity-60 hover:opacity-100 transition-opacity ${headlineText}`}
                aria-label="Refresh"
                title="Refresh"
                whileTap={{ scale: 0.85 }}
              >
                <svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                </svg>
              </motion.button>
            )}
            {/* Yellow dot — matches Window.tsx minimize button */}
            <motion.button
              onClick={(e) => { e.stopPropagation(); setMinimized(!isMinimized); }}
              className="p-1 -m-1 flex items-center justify-center"
              aria-label={isMinimized ? 'Restore Spotify widget' : 'Minimize Spotify widget'}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.85 }}
            >
              <span className="block w-3.5 h-3.5 rounded-full bg-yellow-400 hover:bg-yellow-500 transition-colors" />
            </motion.button>
          </div>
        </div>

        {/* ── Content ── */}
        {!isMinimized && (
          <AnimatePresence mode="wait">
            {isLoading ? (
              /* Loading state */
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col flex-1"
              >
                <div
                  className={`flex-shrink-0 flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                  style={{ width: artSize, height: artSize }}
                >
                  <div className={`rounded-full flex items-center justify-center animate-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                    style={{ width: artSize * 0.28, height: artSize * 0.28 }}
                  >
                    <SpotifyLogo size={Math.round(artSize * 0.12)} />
                  </div>
                </div>
                <div className={`px-3 py-3 space-y-2.5 border-t ${dividerCls}`} style={{ height: INFO_H }}>
                  <Shimmer className="h-3 w-3/4" dark={isDarkMode} />
                  <Shimmer className="h-2.5 w-1/2" dark={isDarkMode} />
                  <Shimmer className="h-1.5 w-full" dark={isDarkMode} />
                </div>
              </motion.div>

            ) : !isConfigured || error || isEmpty ? (
              /* Not configured / error / no server data */
              <motion.a
                key="unconfigured"
                href="https://open.spotify.com/user/12179053622"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col flex-1 no-underline"
                onClick={e => e.stopPropagation()}
              >
                <AlbumPlaceholder size={artSize} isDarkMode={isDarkMode} />
                <div className={`px-3 py-3 border-t ${dividerCls} flex flex-col justify-center`} style={{ height: INFO_H }}>
                  <p className={`text-sm font-semibold truncate ${headlineText}`}>
                    Jimmy on Spotify ↗
                  </p>
                  <p className={`text-xs mt-0.5 truncate ${mutedText}`}>
                    {error
                      ? error
                      : isEmpty
                      ? 'Server token needs refresh — check Vercel env vars'
                      : 'View Spotify profile'}
                  </p>
                </div>
              </motion.a>

            ) : !track ? (
              /* Nothing playing */
              <motion.div
                key="nothing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col flex-1"
              >
                <AlbumPlaceholder size={artSize} isDarkMode={isDarkMode} />
                <div className={`px-3 py-3 border-t ${dividerCls} flex flex-col justify-center`} style={{ height: INFO_H }}>
                  <p className={`text-sm font-semibold ${headlineText}`}>Nothing playing</p>
                  <p className={`text-xs mt-0.5 ${mutedText}`}>Try again in a bit.</p>
                </div>
              </motion.div>

            ) : (
              /* Track playing */
              <motion.a
                key={track.songUrl}
                href={track.songUrl}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col flex-1 no-underline"
                onClick={e => e.stopPropagation()}
              >
                {/* Album art — full square, fully visible */}
                <div className="flex-shrink-0 overflow-hidden" style={{ width: artSize, height: artSize }}>
                  {track.albumArt ? (
                    <img
                      src={track.albumArt}
                      alt={`${track.title} album art`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <AlbumPlaceholder size={artSize} isDarkMode={isDarkMode} />
                  )}
                </div>

                {/* Track info */}
                <div
                  className={`px-3 py-2.5 flex flex-col justify-between border-t ${dividerCls}`}
                  style={{ height: INFO_H }}
                >
                  <div className="min-w-0">
                    <p
                      className="text-[9px] font-bold uppercase tracking-wider truncate leading-none mb-1"
                      style={{ color: captionColor }}
                    >
                      {isLive ? (
                        'Jimmy is listening to'
                      ) : (
                        <>
                          Jimmy last listened to
                          {timeAgo && (
                            <span className={`font-mono font-normal normal-case tracking-normal ${mutedText}`}>
                              {' · '}{timeAgo}
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <MarqueeText
                      text={track.title}
                      className={`text-sm font-semibold leading-tight ${headlineText}`}
                    />
                    <p className={`text-xs truncate mt-0.5 ${mutedText}`}>
                      {track.artist}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className={`flex-1 h-1 rounded-full overflow-hidden ${progressTrack}`}>
                      <motion.div
                        className="h-full rounded-full bg-pink-500"
                        animate={{ width: `${isLive ? progressPct : 0}%` }}
                        transition={{ duration: 0.8, ease: 'linear' }}
                      />
                    </div>
                    <span className={`text-[10px] tabular-nums flex-shrink-0 ${mutedText}`}>
                      {timeLabel}
                    </span>
                  </div>
                </div>
              </motion.a>
            )}
          </AnimatePresence>
        )}
      </motion.div>
    </div>
  );
});

SpotifyWidget.displayName = 'SpotifyWidget';

export default SpotifyWidget;
